import React from "react";
import { motion } from "framer-motion";
import { Section, Container, Eyebrow, RevealHeading, Reveal, TextLink } from "@/components/primitives";
import { PageHero } from "@/components/PageHero";
import { ServiceGrid } from "@/components/ServiceGrid";
import { DeliveryRadar } from "@/components/DeliveryRadar";
import { CTASection } from "@/components/CTASection";
import { Seo } from "@/components/Seo";
import { IMPACT, FRAMEWORK_DIMENSIONS } from "@/data/site";
import { PRACTICE_SERVICES } from "@/data/services";

export default function Impact() {
  const im = IMPACT;
  return (
    <>
      <Seo
        title="Impact & Intelligence | GovX Nigeria"
        description="GovX combines research, data, monitoring and citizen feedback to understand whether programmes are delivering the outcomes they were designed to achieve."
      />
      <PageHero {...im.hero} kicker="Measurement & Evidence" />

      {/* Services */}
      <Section className="py-24 lg:py-32">
        <Container>
          <Eyebrow>Capabilities</Eyebrow>
          <RevealHeading
            lines={["Research. Monitoring.", "Intelligence."]}
            as="h2"
            className="mt-7 max-w-2xl font-display text-3xl font-medium tracking-tight sm:text-5xl"
          />
          <div className="mt-14">
            <ServiceGrid slugs={PRACTICE_SERVICES.Impact} />
          </div>
        </Container>
      </Section>

      {/* GovX Delivery Index */}
      <Section dark className="py-24 lg:py-32">
        <Container className="relative z-10">
          <div className="grid gap-14 lg:grid-cols-[1fr_1fr] lg:items-center">
            <div>
              <Eyebrow dark>GovX Delivery Index</Eyebrow>
              <RevealHeading
                lines={["How Well Is A", "Programme Actually", "Being Delivered?"]}
                as="h2"
                className="mt-7 font-display text-3xl font-medium leading-[1.05] tracking-tight sm:text-5xl"
              />
              <Reveal delay={0.1}>
                <p className="mt-7 max-w-lg text-base leading-relaxed text-govx-grey sm:text-lg">
                  A proprietary framework assessing delivery across seven dimensions — an elegant,
                  decision-ready view of where implementation is strong and where it is breaking down.
                </p>
              </Reveal>
              <div className="mt-8 flex flex-wrap gap-2">
                {FRAMEWORK_DIMENSIONS.map((d) => (
                  <span key={d} className="border border-white/12 px-3 py-1.5 font-mono text-[0.68rem] uppercase tracking-wider text-govx-paper/80">
                    {d}
                  </span>
                ))}
              </div>
              <Reveal delay={0.2}>
                <div className="mt-10">
                  <TextLink to="/reports" dark>Explore GovX Delivery Index</TextLink>
                </div>
              </Reveal>
            </div>

            <Reveal delay={0.15}>
              <div className="border border-white/10 bg-govx-charcoal/40 p-6 lg:p-8">
                <div className="mb-4 flex items-center justify-between">
                  <span className="font-mono text-[0.62rem] uppercase tracking-[0.18em] text-govx-grey">
                    Programme Delivery Index
                  </span>
                  <span className="font-mono text-[0.62rem] text-govx-lime">Illustrative</span>
                </div>
                <DeliveryRadar />
                <p className="mt-4 border-t border-white/10 pt-4 text-xs text-govx-grey">
                  Sample geometry shown to demonstrate the framework. GovX does not fabricate scores.
                </p>
              </div>
            </Reveal>
          </div>
        </Container>
      </Section>

      <CTASection
        eyebrow="Impact & Intelligence"
        title={["If you can't measure it,", "you can't improve it."]}
        cta="Talk to GovX Intelligence"
      />
    </>
  );
}
